import axios from 'axios';
import { API_URL } from '../config/api';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: token ? `Bearer ${token}` : '' };
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

const normalizeCredentials = (data = {}) => ({
  ...data,
  isConnected: data.isConnected === true || data.connected === true,
  oauthLoginAvailable: data.oauthLoginAvailable === true,
  organizationSiteConfigured: data.organizationSiteConfigured === true,
  organizationSiteUrl: data.organizationSiteUrl || '',
});

export const getJiraCredentials = async () => {
  const response = await axios.get(`${API_URL}/jira/credentials`, { headers: authHeaders() });
  return normalizeCredentials(response.data);
};

export const getJiraOrgSettings = async () => {
  const response = await axios.get(`${API_URL}/jira/org-settings`, { headers: authHeaders() });
  const data = response.data || {};
  return {
    siteUrl: data.siteUrl || '',
    configured: data.configured === true,
  };
};

export const saveJiraOrgSettings = async (siteUrl) => {
  try {
    const response = await axios.put(
      `${API_URL}/jira/org-settings`,
      { siteUrl },
      { headers: authHeaders() }
    );
    const data = response.data || {};
    return {
      siteUrl: data.siteUrl || '',
      configured: data.configured === true,
    };
  } catch (error) {
    throw new Error(errorMessage(error, 'Failed to save organization Jira URL'));
  }
};

export const saveJiraCredentials = async ({ siteUrl, email, apiToken }) => {
  try {
    const response = await axios.post(`${API_URL}/jira/credentials`, {
      siteUrl,
      email,
      apiToken,
    }, { headers: authHeaders() });
    return normalizeCredentials(response.data);
  } catch (error) {
    throw new Error(errorMessage(error, 'Failed to connect Jira'));
  }
};

export const testJiraConnection = async () => {
  try {
    const response = await axios.post(`${API_URL}/jira/test-connection`, {}, { headers: authHeaders() });
    return Boolean(response.data?.connected);
  } catch (error) {
    throw new Error(errorMessage(error, 'Jira connection failed'));
  }
};

export const disconnectJira = async () => {
  await axios.delete(`${API_URL}/jira/credentials`, { headers: authHeaders() });
};

export const fetchAssignedJiraIssues = async () => {
  try {
    const response = await axios.get(`${API_URL}/jira/assigned-issues`, {
      headers: authHeaders(),
      params: { maxResults: 50 },
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw new Error(errorMessage(error, 'Failed to load Jira tickets'));
  }
};

export const startJiraOauth = async () => {
  try {
    const response = await axios.post(`${API_URL}/jira/oauth/start`, {}, { headers: authHeaders() });
    return response.data?.authorizationUrl;
  } catch (error) {
    throw new Error(errorMessage(error, 'Could not start Jira login'));
  }
};

export const connectJiraWithAtlassian = () => new Promise(async (resolve, reject) => {
  let popup;
  let closedTimer;
  let finished = false;
  const cleanup = () => {
    window.removeEventListener('message', onMessage);
    if (closedTimer) window.clearInterval(closedTimer);
  };
  const finish = (ok, message) => {
    if (finished) return;
    finished = true;
    cleanup();
    if (ok) resolve();
    else reject(new Error(message || 'Jira login was cancelled'));
  };
  const onMessage = (event) => {
    const data = event?.data;
    if (!data || data.source !== 'workloop-jira-oauth') return;
    finish(Boolean(data.ok), data.message);
  };
  window.addEventListener('message', onMessage);
  try {
    const authorizationUrl = await startJiraOauth();
    popup = window.open(authorizationUrl, 'jira-oauth', 'popup=yes,width=560,height=720');
    if (!popup) {
      finish(false, 'Pop-up was blocked. Allow pop-ups and try again.');
      return;
    }
  } catch (error) {
    finish(false, error.message);
    return;
  }
  closedTimer = window.setInterval(() => {
    if (popup && popup.closed) {
      finish(false, 'Jira login window was closed.');
    }
  }, 500);
});
